import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MousePointer2, Plus, Trash2, Undo2, Upload, Sprout } from 'lucide-react';
import { fromBlob } from 'geotiff';
import proj4 from 'proj4';
import { formatNumber } from '../../utils/erpLocale';
import {
  invocarMotorPythonBloquesPoligonoArchivo,
  type MotorPythonBatchResponse,
  type MotorPythonBatchBlock,
} from './deteccionService';
import GeoMapPreview from '../../components/GeoMapPreview';

type PointNorm = [number, number];
type BlockDraw = { bloque_num: number; puntos: PointNorm[] };

function inferEpsgFromGeoKeys(geoKeys: any): number {
  const projCSCode = Number(geoKeys?.ProjectedCSTypeGeoKey || 0);
  const geogCSCode = Number(geoKeys?.GeographicTypeGeoKey || 0);
  if (projCSCode > 0) return projCSCode;
  if (geogCSCode > 0) return geogCSCode;
  return 0;
}

function toWgs84Corner(epsg: number, x: number, y: number): [number, number] {
  if (epsg === 4326) return [x, y];
  let fromProj = '';
  if (epsg >= 32601 && epsg <= 32660) {
    fromProj = `+proj=utm +zone=${epsg - 32600} +datum=WGS84 +units=m +no_defs`;
  } else if (epsg >= 32701 && epsg <= 32760) {
    fromProj = `+proj=utm +zone=${epsg - 32700} +south +datum=WGS84 +units=m +no_defs`;
  } else if (epsg >= 25828 && epsg <= 25838) {
    fromProj = `+proj=utm +zone=${epsg - 25800} +ellps=GRS80 +units=m +no_defs`;
  } else {
    throw new Error(`CRS EPSG:${epsg} no soportado para vista mapa`);
  }
  const [lng, lat] = proj4(fromProj, 'WGS84', [x, y]);
  return [lng, lat];
}

function clamp01(v: number) {
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(1, v));
}

function pointsToSvg(points: PointNorm[]) {
  return points.map((p) => `${p[0] * 100},${p[1] * 100}`).join(' ');
}

function centroid(points: PointNorm[]) {
  if (points.length === 0) return [0, 0] as const;
  const sx = points.reduce((a, p) => a + p[0], 0);
  const sy = points.reduce((a, p) => a + p[1], 0);
  return [sx / points.length, sy / points.length] as const;
}

export function ConteoPlantasV4SimplePage() {
  const [rasterFile, setRasterFile] = useState<File | null>(null);
  const [rasterRefFile, setRasterRefFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [points, setPoints] = useState<PointNorm[]>([]);
  const [blocks, setBlocks] = useState<BlockDraw[]>([]);
  const [params, setParams] = useState({ minArea: 20, maxArea: 800 });
  const [areaMode, setAreaMode] = useState<'auto' | 'fixed_gsd'>('fixed_gsd');
  const [gsdM, setGsdM] = useState(0.05);
  const [detectMode, setDetectMode] = useState<'adaptive' | 'conteo1'>('conteo1');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<MotorPythonBatchResponse | null>(null);
  const [selectedGeojson, setSelectedGeojson] = useState('');
  const [rasterBounds, setRasterBounds] = useState<[[number, number], [number, number]] | null>(null);
  const [rasterGeorefMsg, setRasterGeorefMsg] = useState('');
  const [previewTab, setPreviewTab] = useState<'imagen' | 'mapa'>('imagen');
  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    let revokedUrl = '';
    let active = true;
    async function buildPreview() {
      if (!rasterFile) {
        setPreviewUrl('');
        setPoints([]);
        setBlocks([]);
        setRasterBounds(null);
        setRasterGeorefMsg('');
        return;
      }
      setPoints([]);
      setBlocks([]);
      setResult(null);
      setSelectedGeojson('');
      setPreviewError('');
      setRasterBounds(null);
      setRasterGeorefMsg('');

      try {
        const isTiff = /\.(tif|tiff)$/i.test(rasterFile.name) || /tiff/i.test(rasterFile.type || '');
        if (!isTiff) {
          const nextUrl = URL.createObjectURL(rasterFile);
          revokedUrl = nextUrl;
          if (active) setPreviewUrl(nextUrl);
          return;
        }

        const tiff = await fromBlob(rasterFile);
        const image = await tiff.getImage();
        try {
          const bbox = image.getBoundingBox();
          const geoKeys = image.getGeoKeys() as any;
          const epsg = inferEpsgFromGeoKeys(geoKeys);
          if (bbox && epsg) {
            const [minX, minY, maxX, maxY] = bbox;
            const corners = [
              toWgs84Corner(epsg, minX, minY),
              toWgs84Corner(epsg, minX, maxY),
              toWgs84Corner(epsg, maxX, minY),
              toWgs84Corner(epsg, maxX, maxY),
            ];
            const lngs = corners.map((c) => c[0]);
            const lats = corners.map((c) => c[1]);
            const swLng = Math.min(...lngs);
            const swLat = Math.min(...lats);
            const neLng = Math.max(...lngs);
            const neLat = Math.max(...lats);
            setRasterBounds([[swLat, swLng], [neLat, neLng]]);
            setRasterGeorefMsg(`Raster georreferenciado EPSG:${epsg}`);
          } else {
            setRasterGeorefMsg('Raster sin EPSG detectado (no se puede ubicar en mapa).');
          }
        } catch (geoErr: any) {
          setRasterGeorefMsg(`No se pudo ubicar raster en mapa: ${geoErr?.message || String(geoErr)}`);
        }
        const width = image.getWidth();
        const height = image.getHeight();
        const samples = image.getSamplesPerPixel();
        const rasters = (await image.readRasters({ interleave: true })) as unknown as ArrayLike<number>;
        const pixels = width * height;
        if (!rasters || rasters.length < pixels) throw new Error('Raster vacio');

        let min = Number.POSITIVE_INFINITY;
        let max = Number.NEGATIVE_INFINITY;
        for (let i = 0; i < rasters.length; i++) {
          const v = Number(rasters[i]);
          if (!Number.isFinite(v)) continue;
          if (v < min) min = v;
          if (v > max) max = v;
        }
        if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
          min = 0;
          max = 1;
        }
        const range = Math.max(1e-6, max - min);
        const to8 = (v: number) => Math.max(0, Math.min(255, Math.round(((v - min) / range) * 255)));

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('No se pudo crear canvas');
        const imgData = ctx.createImageData(width, height);
        for (let i = 0; i < pixels; i++) {
          const dst = i * 4;
          if (samples >= 3) {
            const src = i * samples;
            imgData.data[dst] = to8(Number(rasters[src]));
            imgData.data[dst + 1] = to8(Number(rasters[src + 1]));
            imgData.data[dst + 2] = to8(Number(rasters[src + 2]));
          } else {
            const v = to8(Number(rasters[i]));
            imgData.data[dst] = v;
            imgData.data[dst + 1] = v;
            imgData.data[dst + 2] = v;
          }
          imgData.data[dst + 3] = 255;
        }
        ctx.putImageData(imgData, 0, 0);
        const blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('No se pudo generar preview'))), 'image/png', 0.92);
        });
        const previewBlobUrl = URL.createObjectURL(blob);
        revokedUrl = previewBlobUrl;
        if (active) setPreviewUrl(previewBlobUrl);
      } catch (e: any) {
        if (!active) return;
        setPreviewUrl('');
        setPreviewError(`No se pudo previsualizar TIFF: ${e?.message || String(e)}`);
      }
    }
    buildPreview();
    return () => {
      active = false;
      if (revokedUrl) URL.revokeObjectURL(revokedUrl);
    };
  }, [rasterFile]);

  const nextBlockNum = useMemo(() => {
    if (blocks.length === 0) return 1;
    return Math.max(...blocks.map((b) => b.bloque_num)) + 1;
  }, [blocks]);

  const drawOverlayGeojson = useMemo(() => {
    if (!rasterBounds) return [] as any[];
    const [[swLat, swLng], [neLat, neLng]] = rasterBounds;
    const latSpan = neLat - swLat;
    const lngSpan = neLng - swLng;
    const toLngLat = (p: PointNorm): [number, number] => {
      const lng = swLng + p[0] * lngSpan;
      const lat = neLat - p[1] * latSpan;
      return [lng, lat];
    };
    return blocks.map((b) => {
      const ring = [...b.puntos.map(toLngLat), toLngLat(b.puntos[0])];
      return {
        type: 'Feature',
        properties: { bloque_num: b.bloque_num },
        geometry: { type: 'Polygon', coordinates: [ring] },
      };
    });
  }, [blocks, rasterBounds]);

  const currentDrawGeojson = useMemo(() => {
    if (!rasterBounds || points.length < 3) return null;
    const [[swLat, swLng], [neLat, neLng]] = rasterBounds;
    const latSpan = neLat - swLat;
    const lngSpan = neLng - swLng;
    const ring = [...points, points[0]].map((p) => [swLng + p[0] * lngSpan, neLat - p[1] * latSpan]);
    return { type: 'Polygon', coordinates: [ring] };
  }, [points, rasterBounds]);

  function onImageClick(e: React.MouseEvent<HTMLImageElement>) {
    if (!imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const nx = clamp01((e.clientX - rect.left) / rect.width);
    const ny = clamp01((e.clientY - rect.top) / rect.height);
    setPoints((prev) => [...prev, [nx, ny]]);
  }

  function addBlock() {
    if (points.length < 3) {
      setError('Marca al menos 3 puntos para cerrar un bloque.');
      return;
    }
    setBlocks((prev) => [...prev, { bloque_num: nextBlockNum, puntos: [...points] }]);
    setPoints([]);
    setError('');
  }

  function removeBlock(num: number) {
    setBlocks((prev) => prev.filter((b) => b.bloque_num !== num));
  }

  async function runDetect() {
    if (!rasterFile) {
      setError('Sube primero un archivo RGB/GeoTIFF.');
      return;
    }
    if (blocks.length === 0) {
      setError('Agrega al menos un bloque B# antes de ejecutar.');
      return;
    }
    setRunning(true);
    setError('');
    setResult(null);
    setSelectedGeojson('');
    try {
      const payload = await invocarMotorPythonBloquesPoligonoArchivo(
        params,
        blocks.map((b) => ({ bloque_num: b.bloque_num, puntos: b.puntos })),
        rasterFile,
        rasterRefFile,
        'upload',
        { areaMode, gsdM, detectMode }
      );
      setResult(payload);
      const first = payload.geojson_poligonos?.[0]?.geometry;
      if (first) setSelectedGeojson(JSON.stringify(first, null, 2));
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setRunning(false);
    }
  }

  function showGeojsonForBlock(block: MotorPythonBatchBlock) {
    const geom = block.geometry_export || block.geometry;
    if (!geom) return;
    setSelectedGeojson(JSON.stringify(geom, null, 2));
  }

  return (
    <div className="w-full p-6 space-y-5">
      <div className="rounded-2xl border border-line bg-surface-raised p-5">
        <h2 className="text-xl font-semibold text-ink">Conteo de Plantas V4 (Simple)</h2>
        <p className="text-sm ink-2">Sube MS_NIR para detectar y, opcionalmente, un raster base georreferenciado para exportar GeoJSON en mapa real.</p>
      </div>

      <div className="rounded-2xl border border-line bg-surface-raised p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink">
            <Upload size={14} />
            {rasterFile ? rasterFile.name : 'Subir RGB/GeoTIFF'}
            <input type="file" accept=".tif,.tiff,image/tiff,.png,.jpg,.jpeg" className="hidden" onChange={(e) => setRasterFile(e.target.files?.[0] || null)} />
          </label>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink">
            <Upload size={14} />
            {rasterRefFile ? `Base mapa: ${rasterRefFile.name}` : 'Raster base georreferenciado (opcional)'}
            <input type="file" accept=".tif,.tiff,image/tiff" className="hidden" onChange={(e) => setRasterRefFile(e.target.files?.[0] || null)} />
          </label>

          <input type="number" className="w-24 rounded border border-line bg-surface px-2 py-1 text-sm text-ink" value={params.minArea} onChange={(e) => setParams((p) => ({ ...p, minArea: Math.max(1, Number(e.target.value) || 1) }))} title="Min area" />
          <input type="number" className="w-24 rounded border border-line bg-surface px-2 py-1 text-sm text-ink" value={params.maxArea} onChange={(e) => setParams((p) => ({ ...p, maxArea: Math.max(1, Number(e.target.value) || 1) }))} title="Max area" />
          <select className="rounded border border-line bg-surface px-2 py-1 text-sm text-ink" value={areaMode} onChange={(e) => setAreaMode((e.target.value as 'auto' | 'fixed_gsd') || 'auto')}>
            <option value="auto">Area georreferenciada</option>
            <option value="fixed_gsd">Area modo conteo_1 (GSD fijo)</option>
          </select>
          {areaMode === 'fixed_gsd' && (
            <input type="number" step="0.001" min="0.001" className="w-24 rounded border border-line bg-surface px-2 py-1 text-sm text-ink" value={gsdM} onChange={(e) => setGsdM(Math.max(0.001, Number(e.target.value) || 0.05))} title="GSD m/pixel" />
          )}
          <select className="rounded border border-line bg-surface px-2 py-1 text-sm text-ink" value={detectMode} onChange={(e) => setDetectMode((e.target.value as 'adaptive' | 'conteo1') || 'conteo1')}>
            <option value="conteo1">Motor conteo_1</option>
            <option value="adaptive">Motor adaptativo</option>
          </select>

          <button type="button" onClick={() => setPoints((prev) => prev.slice(0, -1))} disabled={points.length === 0} className="inline-flex items-center gap-2 rounded-lg border border-line px-3 py-2 text-sm ink-2">
            <Undo2 size={14} /> Deshacer punto
          </button>
          <button type="button" onClick={() => setPoints([])} disabled={points.length === 0} className="inline-flex items-center gap-2 rounded-lg border border-line px-3 py-2 text-sm ink-2">
            <Trash2 size={14} /> Limpiar puntos
          </button>
          <button type="button" onClick={addBlock} disabled={points.length < 3} className="inline-flex items-center gap-2 rounded-lg border border-emerald-700/60 bg-emerald-950/30 px-3 py-2 text-sm text-emerald-300 disabled:opacity-60">
            <Plus size={14} /> Agregar B{nextBlockNum}
          </button>
          <button type="button" onClick={runDetect} disabled={!rasterFile || blocks.length === 0 || running} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
            <Sprout size={14} /> {running ? 'Ejecutando...' : 'Ejecutar conteo'}
          </button>
        </div>

        <div className="text-xs ink-2 inline-flex items-center gap-2">
          <MousePointer2 size={12} />
          Clic para vertices del bloque actual. Puntos actuales: {points.length}. Bloques agregados: {blocks.length}.
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded border px-2 py-1 text-xs"
            style={{
              borderColor: previewTab === 'imagen' ? '#16a34a' : 'var(--line)',
              color: previewTab === 'imagen' ? '#86efac' : 'var(--ink-muted)',
              background: previewTab === 'imagen' ? 'rgba(22,163,74,.15)' : 'transparent',
            }}
            onClick={() => setPreviewTab('imagen')}
          >
            Imagen
          </button>
          <button
            type="button"
            className="rounded border px-2 py-1 text-xs"
            style={{
              borderColor: previewTab === 'mapa' ? '#16a34a' : 'var(--line)',
              color: previewTab === 'mapa' ? '#86efac' : 'var(--ink-muted)',
              background: previewTab === 'mapa' ? 'rgba(22,163,74,.15)' : 'transparent',
            }}
            onClick={() => setPreviewTab('mapa')}
            disabled={!rasterBounds}
          >
            Mapa
          </button>
        </div>

        {previewUrl && previewTab === 'imagen' ? (
          <div className="overflow-auto rounded-xl border border-line bg-black/40 p-2">
            <div className="flex justify-center">
              <div className="relative inline-block">
                <img ref={imgRef} src={previewUrl} alt="preview raster" className="block h-auto max-h-[68vh] w-auto max-w-full" onClick={onImageClick} />
                <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                  {blocks.map((b) => {
                    const c = centroid(b.puntos);
                    return (
                      <g key={`blk-${b.bloque_num}`}>
                        <polygon points={pointsToSvg(b.puntos)} fill="rgba(59,130,246,0.24)" stroke="#60a5fa" strokeWidth="0.45" />
                        <text x={c[0] * 100} y={c[1] * 100} fontSize="3.2" fill="#bfdbfe" textAnchor="middle">
                          B{b.bloque_num}
                        </text>
                      </g>
                    );
                  })}
                  {points.length >= 2 && <polyline points={pointsToSvg(points)} fill="rgba(34,197,94,0.22)" stroke="#22c55e" strokeWidth="0.45" />}
                  {points.map((p, idx) => (
                    <g key={`${p[0]}-${p[1]}-${idx}`}>
                      <circle cx={p[0] * 100} cy={p[1] * 100} r="0.75" fill="#22c55e" />
                      <text x={p[0] * 100 + 0.8} y={p[1] * 100 - 0.8} fontSize="2.4" fill="#bbf7d0">
                        {idx + 1}
                      </text>
                    </g>
                  ))}
                </svg>
              </div>
            </div>
          </div>
        ) : previewTab === 'mapa' && rasterBounds ? (
          <div className="rounded-xl border border-line bg-surface p-2">
            <GeoMapPreview
              geojson={currentDrawGeojson || drawOverlayGeojson[0] || null}
              height={420}
              maxWidthClassName="max-w-full"
              polygonColor="#22c55e"
              rasterOverlay={previewUrl ? { url: previewUrl, bounds: rasterBounds, opacity: 0.72 } : null}
              overlays={[
                ...drawOverlayGeojson.map((g) => ({ geojson: g, color: '#60a5fa', flashing: false })),
                ...(currentDrawGeojson ? [{ geojson: currentDrawGeojson, color: '#22c55e', flashing: false }] : []),
                ...((result?.bloques || [])
                  .filter((b) => b.geometry)
                  .map((b) => ({ geojson: b.geometry, color: '#f59e0b', flashing: false }))),
              ]}
            />
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-line bg-surface p-8 text-center text-sm ink-2">
            Sube un archivo para empezar a dibujar.
          </div>
        )}

        {previewError && <div className="rounded-lg border border-yellow-700/50 bg-yellow-950/30 px-3 py-2 text-sm text-yellow-300">{previewError}</div>}
        {rasterGeorefMsg && <div className="rounded-lg border border-sky-800/50 bg-sky-950/20 px-3 py-2 text-xs text-sky-200">{rasterGeorefMsg}</div>}
        {error && <div className="rounded-lg border border-red-800/60 bg-red-950/30 px-3 py-2 text-sm text-red-300">{error}</div>}
        {result?.warning && <div className="rounded-lg border border-amber-800/60 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">{result.warning}</div>}
      </div>

      {rasterBounds && (
        <div className="rounded-2xl border border-line bg-surface-raised p-5">
          <p className="mb-2 text-sm font-semibold text-ink">Mapa de posicion</p>
          <GeoMapPreview
            geojson={(result?.bloques || []).find((b) => b.geometry)?.geometry || null}
            height={320}
            maxWidthClassName="max-w-full"
            polygonColor="#22c55e"
            rasterOverlay={previewUrl ? { url: previewUrl, bounds: rasterBounds, opacity: 0.72 } : null}
            overlays={(result?.bloques || [])
              .filter((b) => b.geometry)
              .map((b) => ({ geojson: b.geometry, color: '#60a5fa', flashing: false }))}
          />
        </div>
      )}

      <div className="rounded-2xl border border-line bg-surface-raised p-5">
        <p className="mb-2 text-sm font-semibold text-ink">Bloques marcados</p>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left ink-2">
              <tr>
                <th className="py-2 pr-3">Bloque</th>
                <th className="py-2 pr-3">Vertices</th>
                <th className="py-2 pr-3">Accion</th>
              </tr>
            </thead>
            <tbody className="text-ink">
              {blocks.map((b) => (
                <tr key={`tbl-${b.bloque_num}`} className="border-t border-line">
                  <td className="py-2 pr-3">B{b.bloque_num}</td>
                  <td className="py-2 pr-3">{b.puntos.length}</td>
                  <td className="py-2 pr-3">
                    <button className="rounded border border-red-800/60 px-2 py-1 text-xs text-red-300" onClick={() => removeBlock(b.bloque_num)}>
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
              {blocks.length === 0 && (
                <tr className="border-t border-line">
                  <td className="py-2 pr-3 ink-2" colSpan={3}>
                    Sin bloques.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {result && (
        <div className="rounded-2xl border border-line bg-surface-raised p-5 space-y-3">
          <p className="text-sm ink-2">{result.mensaje || 'Resultado por bloques'}</p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left ink-2">
                <tr>
                  <th className="py-2 pr-3">Bloque</th>
                  <th className="py-2 pr-3">Area ha</th>
                  <th className="py-2 pr-3">Plantas</th>
                  <th className="py-2 pr-3">Hileras</th>
                  <th className="py-2 pr-3">Long. m</th>
                  <th className="py-2 pr-3">Densidad</th>
                  <th className="py-2 pr-3">KPI</th>
                  <th className="py-2 pr-3">Coord.</th>
                  <th className="py-2 pr-3">GeoJSON</th>
                </tr>
              </thead>
              <tbody className="text-ink">
                {(result.bloques || []).map((row, idx) => (
                  <tr key={`res-${row.bloque_num || idx}`} className="border-t border-line">
                    <td className="py-2 pr-3">B{row.bloque_num || '-'}</td>
                    <td className="py-2 pr-3">{Number(row.area_ha || 0).toFixed(4)}</td>
                    <td className="py-2 pr-3">{formatNumber(Number(row.conteo || 0))}</td>
                    <td className="py-2 pr-3">{formatNumber(Number(row.hileras || 0))}</td>
                    <td className="py-2 pr-3">{Number(row.longitud_m || 0).toFixed(1)}</td>
                    <td className="py-2 pr-3">{Number(row.densidad || 0).toFixed(0)} pl/ha</td>
                    <td className="py-2 pr-3">{Number(row.kpi || 0).toFixed(1)}%</td>
                    <td className="py-2 pr-3">{row.coord_space || '-'}</td>
                    <td className="py-2 pr-3">
                      {(row.geometry_export || row.geometry) ? (
                        <button className="rounded border border-line px-2 py-1 text-xs text-sky-300" onClick={() => showGeojsonForBlock(row)}>
                          Ver B{row.bloque_num}
                        </button>
                      ) : (
                        <span className="text-red-300 text-xs">Sin geometria</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            {result.resultado_reporte_url && (
              <a href={result.resultado_reporte_url} target="_blank" rel="noreferrer" className="text-emerald-300 underline">
                Abrir reporte
              </a>
            )}
            {result.resultado_json_url && (
              <a href={result.resultado_json_url} target="_blank" rel="noreferrer" className="text-blue-300 underline">
                Abrir JSON consolidado
              </a>
            )}
          </div>
          {selectedGeojson && (
            <div className="rounded-lg border border-line bg-surface p-3">
              <p className="mb-2 text-xs ink-2">GeoJSON Polygon (coordenadas reales)</p>
              <pre className="max-h-72 overflow-auto text-xs ink-2 whitespace-pre-wrap">{selectedGeojson}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ConteoPlantasV4SimplePage;
